import { Button, Grid, Typography } from '@mui/material';
import RideInfoItem from 'components/scheduler/RideInfoItem';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { differenceWith, isEqual } from 'lodash-es';
import {
  useDeleteOperatorsRidesLegsMutation,
  usePostOperatorsRidesLegsMutation,
} from 'api/queries/operators';
import {
  GoogleConf,
  LegSchemaType,
  RideLegsSchemaType,
  colors,
  emptyLegObject,
  rideLegsSchema,
} from 'remote/global';
import { runSequentially } from 'utils/helpers';
import { useEffect } from 'react';

type Props = {
  defaultValues: RideLegsSchemaType;
  googleConfig: GoogleConf;
  rideId: number;
  isOrderEditAvailable: boolean;
};

type LegWithIndex = {
  leg: LegSchemaType;
  legIndex: number;
};

const convertLegWithIndexes = (legs: LegSchemaType[]): LegWithIndex[] => {
  return legs.map((leg, legIndex) => ({ leg, legIndex }));
};

function BookingEditRideJourneyForm(props: Props) {
  const { defaultValues, rideId, googleConfig, isOrderEditAvailable } = props;
  const { t } = useTranslation();

  const formInstance = useForm<RideLegsSchemaType>({
    defaultValues: {
      legs: defaultValues.legs ?? [],
    },
    resolver: zodResolver(rideLegsSchema),
  });
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;
  const deleteLegsMutation = useDeleteOperatorsRidesLegsMutation(String(rideId));
  const postLegsMutation = usePostOperatorsRidesLegsMutation(String(rideId));

  useEffect(() => {
    formInstance.reset({ legs: defaultValues.legs ?? [] });
  }, [rideId, defaultValues.legs]);

  const { fields, remove, append, prepend } = useFieldArray({
    name: 'legs',
    keyName: 'fieldPropertyId',
    control,
  });

  const handleAddRow = (legIndex: number) => {
    // Currently it can either be appended or prepended
    // If legIndex is greater than 0 it is the dropoff location
    if (legIndex <= 0) {
      // Insert the first EMPTY_LEG and copy over the previous object values
      const firstLeg = fields[legIndex];
      const newStartLeg: LegSchemaType = {
        ...emptyLegObject,
        endLocation: firstLeg.startLocation,
        endTime: firstLeg.startTime!,
      };
      prepend(newStartLeg);
    } else {
      // Insert the last EMPTY_LEG and copy over the previous object values
      const lastLeg = fields[fields.length - 1];
      const newEndLeg: LegSchemaType = {
        ...emptyLegObject,
        startLocation: lastLeg.endLocation!,
        startTime: lastLeg.endTime,
        endTime: lastLeg.endTime, // TODO: Temp fix to allow adding new rows with this schema
      };
      append(newEndLeg);
    }
  };

  const onValid = async (data: RideLegsSchemaType) => {
    const defaultLegs = [...(convertLegWithIndexes(defaultValues.legs) ?? [])];
    const newLegs = convertLegWithIndexes(data.legs);

    const inserted = differenceWith(newLegs, defaultLegs, (a, b) => isEqual(a.leg, b.leg));
    const removed = differenceWith(defaultLegs, newLegs, (a, b) => isEqual(a.leg, b.leg)).sort(
      (a, b) => b.legIndex - a.legIndex // Makes sure that highest legIndex comes first, we reverse this array because we want to remove indexes from last to first, the requests are run sequentially which means indexes change after each request
    );

    try {
      const removedPromises = removed.map(
        ({ legIndex }) =>
          () =>
            deleteLegsMutation.mutateAsync(legIndex)
      );
      const insertedPromises = inserted.map(
        ({ leg, legIndex }) =>
          () =>
            postLegsMutation.mutateAsync({ ...leg, legIndex })
      );

      // Call the promise functions sequentially
      await runSequentially([...removedPromises, ...insertedPromises]);
    } catch (e) {
      // TODO: SENTRY CAPTURE RROR
    }
  };

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid container gap={4} sx={{ padding: [null, null, '2rem'] }}>
        <Grid container item spacing={1} gap={1} sx={{ paddingLeft: '25px', position: 'relative' }}>
          <Typography
            sx={{ color: colors.uiBlue600, mb: '12px', fontWeight: 600, fontSize: '18px' }}
          >
            {t('modals.editOrder.tabs.ride')}
          </Typography>
          {fields.map((leg, legIndex) => {
            return (
              <RideInfoItem
                isOrderEditAvailable={isOrderEditAvailable}
                key={leg.fieldPropertyId}
                form={formInstance}
                leg={leg}
                legIndex={legIndex}
                legs={fields}
                googleConfig={googleConfig}
                onRemoveRow={(idx) => remove(idx)}
                onAddRow={(idx) => handleAddRow(idx)}
              />
            );
          })}
        </Grid>
        <Button
          type='submit'
          disabled={!isOrderEditAvailable || isSubmitting || isValidating || isLoading}
          variant='contained'
          fullWidth
          sx={{ mr: [0, '4rem'], maxWidth: 'max-content', ml: 'auto' }}
        >
          {t('save')}
        </Button>
      </Grid>
    </form>
  );
}

export default BookingEditRideJourneyForm;
